// Home - Hero (sections/home-hero.liquid)
// Crossfade slideshow with autoplay, dots, swipe and theme editor support

const HOME_HERO_SWIPE_THRESHOLD = 40

class HomeHero extends HTMLElement {
	connectedCallback() {
		this.slides = [...this.querySelectorAll("[data-hh-slide]")]
		this.contents = [...this.querySelectorAll("[data-hh-content]")]
		this.dots = [...this.querySelectorAll("[data-hh-dot]")]
		this.index = 0
		this.timer = null
		this.pauses = new Set()

		if (this.slides.length < 2) return

		let reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
		this.interval = Number(this.dataset.interval) || 6000
		this.autoplay = this.dataset.autoplay === "true" && !reducedMotion

		this.dots.forEach((dot, i) => dot.addEventListener("click", () => this.go(i)))

		this.addEventListener("mouseenter", () => this.pause("hover"))
		this.addEventListener("mouseleave", () => this.resume("hover"))
		this.addEventListener("focusin", () => this.pause("focus"))
		this.addEventListener("focusout", event => {
			if (!this.contains(event.relatedTarget)) this.resume("focus")
		})

		this.addEventListener("pointerdown", this.onPointerDown)
		this.addEventListener("click", this.onClickCapture, true)
		document.addEventListener("visibilitychange", this.onVisibilityChange)

		if (window.Shopify?.designMode) {
			document.addEventListener("shopify:block:select", this.onBlockSelect)
			document.addEventListener("shopify:block:deselect", this.onBlockDeselect)
		}

		this.schedule()
	}

	disconnectedCallback() {
		clearTimeout(this.timer)
		document.removeEventListener("visibilitychange", this.onVisibilityChange)
		document.removeEventListener("shopify:block:select", this.onBlockSelect)
		document.removeEventListener("shopify:block:deselect", this.onBlockDeselect)
	}

	go(target) {
		let count = this.slides.length
		let next = (target + count) % count

		if (next !== this.index) {
			this.setActive(this.index, false)
			this.setActive(next, true)
			this.index = next
		}

		this.schedule()
	}

	setActive(i, isActive) {
		let slide = this.slides[i]
		let content = this.contents[i]
		let dot = this.dots[i]

		slide.classList.toggle("is-active", isActive)
		slide.inert = !isActive
		content?.classList.toggle("is-active", isActive)
		if (content) content.inert = !isActive

		if (isActive) dot?.setAttribute("aria-current", "true")
		else dot?.removeAttribute("aria-current")
	}

	schedule() {
		clearTimeout(this.timer)
		if (!this.autoplay || this.pauses.size > 0) return
		this.timer = setTimeout(() => this.go(this.index + 1), this.interval)
	}

	pause(reason) {
		this.pauses.add(reason)
		clearTimeout(this.timer)
	}

	resume(reason) {
		this.pauses.delete(reason)
		this.schedule()
	}

	onVisibilityChange = () => {
		if (document.hidden) this.pause("hidden")
		else this.resume("hidden")
	}

	onPointerDown = event => {
		this.swiped = false
		if (event.pointerType === "mouse") return

		let startX = event.clientX
		let startY = event.clientY

		let onUp = upEvent => {
			let deltaX = upEvent.clientX - startX
			let deltaY = upEvent.clientY - startY

			if (Math.abs(deltaX) > HOME_HERO_SWIPE_THRESHOLD && Math.abs(deltaX) > Math.abs(deltaY)) {
				this.swiped = true
				this.go(this.index + (deltaX < 0 ? 1 : -1))
			}
		}

		this.addEventListener("pointerup", onUp, { once: true })
		this.addEventListener("pointercancel", () => this.removeEventListener("pointerup", onUp), { once: true })
	}

	// A swipe ends with a click on the slide link: swallow it
	onClickCapture = event => {
		if (!this.swiped) return
		this.swiped = false
		event.preventDefault()
		event.stopPropagation()
	}

	onBlockSelect = event => {
		let i = this.slides.indexOf(event.target)
		if (i === -1) return
		this.pause("editor")
		this.go(i)
	}

	onBlockDeselect = event => {
		if (this.slides.includes(event.target)) this.resume("editor")
	}
}

if (!customElements.get("home-hero")) customElements.define("home-hero", HomeHero)
